import { useCallback, useRef, type KeyboardEvent } from "react";
import {
  Table2,
  GitBranch,
  BarChart3,
  ShieldCheck,
  AlertTriangle,
  History,
} from "lucide-react";
import { cn } from "../../lib/utils";

export type ViewType =
  | "tables"
  | "relationships"
  | "summary"
  | "rules"
  | "findings"
  | "history";

interface ViewTab {
  id: ViewType;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
}

interface ViewToggleBarProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  disabledViews?: Partial<Record<ViewType, string>>;
  counts?: Partial<Record<ViewType, number>>;
}

// Tab order reflects the user's reading flow on arrival:
//   Summary  — "what's the overall health of this dataset?"  (landing)
//   Tables    — "show me the table grid to drill into specifics"
//   Relationships — "how do these tables connect?"
//   Findings  — "what concrete issues need attention?"  (before Rules so
//               the user can triage issues before deciding to codify rules)
//   Rules     — "what DQ rules apply / can I promote findings to?"
const TABS: ViewTab[] = [
  { id: "summary", label: "Summary", icon: <BarChart3 className="w-4 h-4" /> },
  { id: "tables", label: "Tables", icon: <Table2 className="w-4 h-4" /> },
  {
    id: "relationships",
    label: "Relationships",
    icon: <GitBranch className="w-4 h-4" />,
  },
  {
    id: "findings",
    label: "Findings",
    icon: <AlertTriangle className="w-4 h-4" />,
  },
  { id: "rules", label: "Rules", icon: <ShieldCheck className="w-4 h-4" /> },
  // Run history & comparison — last in the row because it's a
  // cross-cutting view ("how did we get here?") not a drill-down view.
  { id: "history", label: "History", icon: <History className="w-4 h-4" /> },
];

export function ViewToggleBar({
  activeView,
  onViewChange,
  disabledViews = {},
  counts = {},
}: ViewToggleBarProps) {
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const enabledTabs = TABS.filter((t) => !disabledViews[t.id]);
      const currentIdx = enabledTabs.findIndex((t) => t.id === activeView);

      let nextIdx = -1;
      if (e.key === "ArrowRight") {
        nextIdx = (currentIdx + 1) % enabledTabs.length;
      } else if (e.key === "ArrowLeft") {
        nextIdx = (currentIdx - 1 + enabledTabs.length) % enabledTabs.length;
      }

      if (nextIdx >= 0) {
        e.preventDefault();
        const nextTab = enabledTabs[nextIdx];
        onViewChange(nextTab.id);
        const globalIdx = TABS.findIndex((t) => t.id === nextTab.id);
        tabsRef.current[globalIdx]?.focus();
      }
    },
    [activeView, onViewChange, disabledViews],
  );

  return (
    <div
      role="tablist"
      aria-label="Data views"
      className="flex gap-1 p-1 rounded-lg glass-surface-input"
      onKeyDown={handleKeyDown}
    >
      {TABS.map((tab, i) => {
        const isActive = activeView === tab.id;
        const isDisabled = !!disabledViews[tab.id];

        return (
          <button
            key={tab.id}
            ref={(el) => {
              tabsRef.current[i] = el;
            }}
            role="tab"
            // Explicit aria-label so the count badge inside the button
            // doesn't contaminate the accessible name (which selectors
            // like `get_by_role("tab", name="Tables", exact=true)` rely on).
            aria-label={tab.label}
            aria-selected={isActive}
            aria-disabled={isDisabled || undefined}
            tabIndex={isActive ? 0 : -1}
            title={isDisabled ? disabledViews[tab.id] : undefined}
            onClick={() => !isDisabled && onViewChange(tab.id)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
              isActive &&
                "text-[var(--primary)] border-b-2 border-[var(--primary)] font-semibold bg-white/5",
              !isActive &&
                !isDisabled &&
                "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
              isDisabled && "opacity-40 cursor-not-allowed",
            )}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
            {typeof counts[tab.id] === "number" && (
              <span
                aria-hidden="true"
                className={cn(
                  "ml-1 text-[11px] px-1.5 py-0.5 rounded-full tabular-nums",
                  isActive
                    ? "bg-[var(--primary)]/20 text-[var(--primary)]"
                    : "bg-white/10 text-white/55",
                )}
              >
                {counts[tab.id]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
